import {
  INSTANCE_EATD_TEST_OPERATOR_ADD,
  INSTANCE_EATD_TEST_OPERATOR_REMOVE,
  INSTANCE_EATD_PREVIEW_OPERATOR_REQUEST,
  INSTANCE_EATD_PREVIEW_OPERATOR_SUCCESS,
  INSTANCE_EATD_PREVIEW_OPERATOR_FAILURE,
  INSTANCE_EATD_ANONYMIZE_OPERATOR_REQUEST,
  INSTANCE_EATD_ANONYMIZE_OPERATOR_SUCCESS,
  INSTANCE_EATD_ANONYMIZE_OPERATOR_FAILURE,
  EATD_TEST_INCIDENT_REMOVE,
} from 'redux/eatd/constants';
import { reduceByTypes } from 'redux/apiHelpers';
import _ from 'lodash';
import * as testActions from 'redux/eatd/testActions'; // Import actions to reference types

const defaultState = {
  isAddingOperator: false,
  isAddingIncident: false,
  isAddingPerson: false,
  isAddingChange: false,
  isCountingOperatorMatches: false,
  isCountingIncidentMatches: false,
  isCountingPersonMatches: false,
  isCountingChangeMatches: false,
  addIncidentError: null,
  addPersonError: null,
  addChangeError: null,
  operatorMatchCount: null,
  incidentMatchCount: null,
  personMatchCount: null,
  changeMatchCount: null,
  operatorMatchCountError: null,
  incidentMatchCountError: null,
  personMatchCountError: null,
  changeMatchCountError: null,
  error: null,
  topdeskOperators: {},
  testIncidents: {},
  testPersons: {},
  testChanges: {},
};

// --- Actions constants used in testActions.js ---
const INSTANCE_EATD_TEST_INCIDENT_ADD = testActions.INSTANCE_EATD_TEST_INCIDENT_ADD;
const INSTANCE_EATD_APPLY_CHANGES_INCIDENT = testActions.INSTANCE_EATD_APPLY_CHANGES_INCIDENT;
const INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT = testActions.INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT;
const INSTANCE_EATD_COUNT_MATCHING_OPERATORS = testActions.INSTANCE_EATD_COUNT_MATCHING_OPERATORS;
const INSTANCE_EATD_COUNT_MATCHING_INCIDENTS = testActions.INSTANCE_EATD_COUNT_MATCHING_INCIDENTS;
const INSTANCE_EATD_COUNT_MATCHING_PERSONS = testActions.INSTANCE_EATD_COUNT_MATCHING_PERSONS;
const INSTANCE_EATD_TEST_CHANGE_ADD = testActions.INSTANCE_EATD_TEST_CHANGE_ADD;
const INSTANCE_EATD_APPLY_CHANGES_CHANGE = testActions.INSTANCE_EATD_APPLY_CHANGES_CHANGE;
const INSTANCE_EATD_PREVIEW_CHANGES_CHANGE = testActions.INSTANCE_EATD_PREVIEW_CHANGES_CHANGE;
const INSTANCE_EATD_COUNT_MATCHING_CHANGES = testActions.INSTANCE_EATD_COUNT_MATCHING_CHANGES;

// --- End action constants ---

/**
 * Helper to update a specific person's state within the testPersons map.
 * @param {object} state - Current state.
 * @param {string} personId - ID of the person to update.
 * @param {object} updates - Object containing updates to merge.
 * @returns {object} New state object.
 */
const updatePersonState = (state, personId, updates) => {
  if (!state.testPersons?.[personId]) {
    return state; // Return current state if person doesn't exist
  }
  return {
    ...state,
    testPersons: {
      ...state.testPersons,
      [personId]: {
        ...state.testPersons[personId],
        ...updates,
      },
    },
    error: null, // Clear general error when a specific item action progresses
  };
};

/**
 * Helper to update a specific incident's state within the testIncidents map.
 * @param {object} state - Current state.
 * @param {string} incidentId - ID of the incident to update.
 * @param {object} updates - Object containing updates to merge.
 * @returns {object} New state object.
 */
const updateIncidentState = (state, incidentId, updates) => {
  return {
    ...state,
    testIncidents: {
      ...state.testIncidents,
      [incidentId]: {
        ...state.testIncidents[incidentId],
        ...updates,
      },
    },
    error: null, // Clear general error when a specific item action progresses
  };
};

const updateChangeState = (state, changeId, updates) => {
  return {
    ...state,
    testChanges: {
      ...state.testChanges,
      [changeId]: {
        ...state.testChanges[changeId],
        ...updates,
      },
    },
    error: null,
  };
};

const buildIncidentTestItem = (incidentId, incident = {}) => ({
  id: incidentId,
  number: incident.number || incidentId,
  name: incident.number || incidentId,
  isPreviewing: false,
  isApplying: false,
  error: null,
  previewResult: null,
  applyResult: null,
});

const buildPersonName = (person = {}, fallbackId) => {
  if (person.dynamicName) return person.dynamicName;
  const fullName = [person.firstName, person.surName].filter(Boolean).join(' ');
  return fullName || fallbackId;
};

const buildPersonTestItem = (personId, person = {}) => ({
  id: personId,
  name: buildPersonName(person, personId),
  isPreviewing: false,
  isApplying: false,
  error: null,
  previewResult: null,
  applyResult: null,
});

const buildChangeTestItem = (changeId, change = {}) => ({
  id: changeId,
  number: change.number || changeId,
  name: change.number || changeId,
  isPreviewing: false,
  isApplying: false,
  error: null,
  previewResult: null,
  applyResult: null,
});

/**
 * Helper to handle Request actions for a specific person.
 * @param {object} state - Current state.
 * @param {object} action - Redux action.
 * @param {string} loadingFlag - Name of the loading flag (e.g., 'isApplying', 'isPreviewing').
 * @returns {object} New state object.
 */
const handlePersonRequest = (state, action, loadingFlag) => {
  const { personId } = action.meta;
  // Ensure the person exists before trying to update
  if (!state.testPersons?.[personId]) {
    // Optionally set a general error? For now, just return state.
    return state; 
  }
  // Update the specific person's state to indicate loading
  return updatePersonState(state, personId, {
    [loadingFlag]: true,
    error: null,          // Clear previous specific error
    applyResult: null,    // Clear previous results
    previewResult: null,
  });
};

/**
 * Helper to handle Failure actions for a specific person.
 * @param {object} state - Current state.
 * @param {object} action - Redux action.
 * @param {string} loadingFlag - Name of the loading flag to clear.
 * @returns {object} New state object.
 */
const handlePersonFailure = (state, action, loadingFlag) => {
  const { personId } = action.meta;
  // Ensure the person exists before trying to update
  if (!state.testPersons?.[personId]) {
    // If the person doesn't exist (e.g., removed before failure), set general error
    return {
      ...state,
      error: action.payload, 
    };
  }
  // Update the specific person's state with the error
  return updatePersonState(state, personId, {
    [loadingFlag]: false,
    error: action.payload, // Store error specific to this person
  });
};

export default function testReducer (state = defaultState, action) {
  // Handle the first reducer's functionality with reduceByTypes

  state = reduceByTypes(INSTANCE_EATD_TEST_OPERATOR_ADD, state, action, {
    requestProp: 'isAddingOperator',
    errorProp: 'error',
    successProp: (currentState, action) => {
      // Access the backend payload nested within the action object
      const operatorData = action?.payload?.topdeskOperator;
      const operatorId = operatorData?.id;

      // Only update operators if valid data and ID are present
      const updatedOperators = operatorId 
        ? { ...currentState.topdeskOperators, [operatorId]: operatorData }
        : currentState.topdeskOperators; // Otherwise, keep existing operators

      return {
        isAddingOperator: false, // Always reset loading state
        error: null, // Always clear error on success
        topdeskOperators: updatedOperators, // Apply update (or lack thereof)
      };
    },
  });

  if (action.type === INSTANCE_EATD_ANONYMIZE_OPERATOR_REQUEST) {
    const { topdeskOperatorId } = action.meta;
    if (!state.topdeskOperators[topdeskOperatorId]) return state; // Operator not found
    return {
      ...state,
      topdeskOperators: _.set({ ...state.topdeskOperators }, topdeskOperatorId, {
        ...(state.topdeskOperators[topdeskOperatorId] || {}),
        isAnonymizing: true, 
        anonymizationError: null,
        anonymizationResult: null,
      }),
    };
  }

  if (action.type === INSTANCE_EATD_ANONYMIZE_OPERATOR_SUCCESS) {
    const { topdeskOperatorId } = action.meta;
    if (!state.topdeskOperators[topdeskOperatorId]) return state; // Operator not found
    return {
      ...state,
      topdeskOperators: _.set({ ...state.topdeskOperators }, topdeskOperatorId, {
        ...(state.topdeskOperators[topdeskOperatorId] || {}),
        isAnonymizing: false,
        anonymizationError: null,
        anonymizationResult: action.payload,
      }),
    };
  }

  if (action.type === INSTANCE_EATD_ANONYMIZE_OPERATOR_FAILURE) {
    const { topdeskOperatorId } = action.meta;
    if (!state.topdeskOperators[topdeskOperatorId]) return state; // Operator not found
    return {
      ...state,
      topdeskOperators: _.set({ ...state.topdeskOperators }, topdeskOperatorId, {
        ...(state.topdeskOperators[topdeskOperatorId] || {}),
        isAnonymizing: false,
        anonymizationError: action.payload,
        anonymizationResult: null,
      }),
    };
  }

  if (action.type === INSTANCE_EATD_PREVIEW_OPERATOR_REQUEST) {
    const { topdeskOperatorId } = action.meta;
    if (!state.topdeskOperators[topdeskOperatorId]) return state;
    return {
      ...state,
      topdeskOperators: _.set({ ...state.topdeskOperators }, topdeskOperatorId, {
        ...(state.topdeskOperators[topdeskOperatorId] || {}),
        isPreviewing: true,
        previewError: null,
        previewResult: null,
      }),
    };
  }

  if (action.type === INSTANCE_EATD_PREVIEW_OPERATOR_SUCCESS) {
    const { topdeskOperatorId } = action.meta;
    if (!state.topdeskOperators[topdeskOperatorId]) return state;
    const previewData = action.payload?.data || {};
    return {
      ...state,
      topdeskOperators: _.set({ ...state.topdeskOperators }, topdeskOperatorId, {
        ...(state.topdeskOperators[topdeskOperatorId] || {}),
        isPreviewing: false,
        previewError: null,
        previewResult: action.payload,
        notices: action.payload?.notices || [],
        topdeskOperator: previewData.original || state.topdeskOperators[topdeskOperatorId],
      }),
    };
  }

  if (action.type === INSTANCE_EATD_PREVIEW_OPERATOR_FAILURE) {
    const { topdeskOperatorId } = action.meta;
    if (!state.topdeskOperators[topdeskOperatorId]) return state;
    return {
      ...state,
      topdeskOperators: _.set({ ...state.topdeskOperators }, topdeskOperatorId, {
        ...(state.topdeskOperators[topdeskOperatorId] || {}),
        isPreviewing: false,
        previewError: action.payload,
        previewResult: null,
      }),
    };
  }

  if (action.type === `${INSTANCE_EATD_APPLY_CHANGES_INCIDENT}_REQUEST`) {
    const { incidentId } = action.meta;
    return updateIncidentState(state, incidentId, {
      isApplying: true,
      error: null, 
      applyResult: null, 
    });
  }

  if (action.type === `${INSTANCE_EATD_APPLY_CHANGES_INCIDENT}_SUCCESS`) {
    const { incidentId } = action.meta;
    return updateIncidentState(state, incidentId, {
      isApplying: false,
      error: null,
      applyResult: action.payload,
    });
  }

  if (action.type === `${INSTANCE_EATD_APPLY_CHANGES_INCIDENT}_FAILURE`) {
    const { incidentId } = action.meta;
    return updateIncidentState(state, incidentId, {
      isApplying: false,
      error: action.payload,
      applyResult: null,
    });
  }

  // Merged switch cases from both reducers
  switch (action.type) {
    case INSTANCE_EATD_TEST_OPERATOR_REMOVE: {
      const { topdeskOperatorId: removeId } = action.meta || { topdeskOperatorId: action.topdeskOperatorId }; // Handle potential variation
      if (!removeId) return state;
      return {
        ...state,
        topdeskOperators: _.omit(state.topdeskOperators, removeId),
      };
    }

    case `${INSTANCE_EATD_COUNT_MATCHING_OPERATORS}_REQUEST`:
      return {
        ...state,
        isCountingOperatorMatches: true,
        operatorMatchCountError: null,
        error: null,
      };

    case `${INSTANCE_EATD_COUNT_MATCHING_OPERATORS}_SUCCESS`:
      return {
        ...state,
        isCountingOperatorMatches: false,
        operatorMatchCountError: null,
        operatorMatchCount: action.payload?.count ?? 0,
        error: null,
      };

    case `${INSTANCE_EATD_COUNT_MATCHING_OPERATORS}_FAILURE`:
      return {
        ...state,
        isCountingOperatorMatches: false,
        operatorMatchCountError: action.payload,
        error: action.payload,
      };

    case `${INSTANCE_EATD_TEST_INCIDENT_ADD}_REQUEST`: {
      return {
        ...state,
        isAddingIncident: true,
        addIncidentError: null,
        error: null,
      };
    }

    case `${INSTANCE_EATD_TEST_INCIDENT_ADD}_SUCCESS`: {
      const { incidentId: addedIncidentId } = action.meta;
      const incidentData = action.payload?.topdeskIncident || {};
      if (!addedIncidentId) {
        return {
          ...state,
          isAddingIncident: false,
        };
      }
      return {
        ...state,
        isAddingIncident: false,
        addIncidentError: null,
        error: null,
        testIncidents: {
          ...state.testIncidents,
          [addedIncidentId]: {
            ...(state.testIncidents[addedIncidentId] || {}),
            ...buildIncidentTestItem(addedIncidentId, incidentData),
          },
        },
      };
    }

    case `${INSTANCE_EATD_TEST_INCIDENT_ADD}_FAILURE`: {
      return {
        ...state,
        isAddingIncident: false,
        addIncidentError: action.payload,
        error: action.payload,
      };
    }

    case `${INSTANCE_EATD_COUNT_MATCHING_INCIDENTS}_REQUEST`: {
      return {
        ...state,
        isCountingIncidentMatches: true,
        incidentMatchCountError: null,
        error: null,
      };
    }

    case `${INSTANCE_EATD_COUNT_MATCHING_INCIDENTS}_SUCCESS`: {
      return {
        ...state,
        isCountingIncidentMatches: false,
        incidentMatchCountError: null,
        incidentMatchCount: action.payload?.count ?? 0,
        error: null,
      };
    }

    case `${INSTANCE_EATD_COUNT_MATCHING_INCIDENTS}_FAILURE`: {
      return {
        ...state,
        isCountingIncidentMatches: false,
        incidentMatchCountError: action.payload,
        error: action.payload,
      };
    }

    case `${INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT}_REQUEST`: {
      const { incidentId } = action.meta;
      return updateIncidentState(state, incidentId, {
        isPreviewing: true,
        isLoading: true,
        error: null,
      });
    }

    case `${INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT}_SUCCESS`: {
      const { incidentId } = action.meta;
      return updateIncidentState(state, incidentId, {
        isPreviewing: false,
        isLoading: false,
        error: null,
        previewResult: action.payload,
        number: action.payload?.data?.original?.number || state.testIncidents?.[incidentId]?.number,
        name: action.payload?.data?.original?.number || incidentId,
      });
    }

    case `${INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT}_FAILURE`: {
      const { incidentId } = action.meta;
      return updateIncidentState(state, incidentId, {
        isPreviewing: false,
        isLoading: false,
        error: action.payload,
      });
    }

    case EATD_TEST_INCIDENT_REMOVE: {
      const { incidentId: removedIncidentId } = action.payload;
      if (!removedIncidentId) {
        return state;
      }
      return {
        ...state,
        testIncidents: _.omit(state.testIncidents, removedIncidentId),
        error: null, // Clear general error on successful remove
      };
    }

    // Handle additional incident-related cases as needed

    // --- BEGIN: Added Person Test Cases --- 

    // Add Person (Initial fetch via Preview)
    case `${testActions.INSTANCE_EATD_TEST_PERSON_ADD}_REQUEST`: {
      return {
        ...state,
        isAddingPerson: true,
        addPersonError: null,
        error: null,
      };
    }

    case `${testActions.INSTANCE_EATD_TEST_PERSON_ADD}_SUCCESS`: {
      const addPersonId = action.meta.personId;
      const personData = action.payload?.topdeskPerson || {};
      return {
        ...state,
        isAddingPerson: false,
        addPersonError: null,
        testPersons: {
          ...state.testPersons,
          [addPersonId]: {
            ...(state.testPersons[addPersonId] || {}),
            ...buildPersonTestItem(addPersonId, personData),
          },
        },
        error: null,
      };
    }

    case `${testActions.INSTANCE_EATD_TEST_PERSON_ADD}_FAILURE`:
      return {
        ...state,
        isAddingPerson: false,
        addPersonError: action.payload,
        error: action.payload,
      };

    // Remove Person
    case testActions.INSTANCE_EATD_TEST_PERSON_REMOVE: {
      const removePersonId = action.payload.personId;
      return {
        ...state,
        testPersons: _.omit(state.testPersons, removePersonId),
        error: null, // Clear general error on successful remove
      };
    }

    // Apply Changes to Person
    case `${testActions.INSTANCE_EATD_APPLY_CHANGES_PERSON}_REQUEST`:
      return handlePersonRequest(state, action, 'isApplying');

    case `${testActions.INSTANCE_EATD_APPLY_CHANGES_PERSON}_SUCCESS`: {
      const applyPersonId = action.meta.personId;
      // Check if person exists before update (might have been removed)
      if (!state.testPersons?.[applyPersonId]) return state;

      return updatePersonState(state, applyPersonId, {
        isApplying: false,
        applyResult: action.payload,
        error: null,
      });
    }

    case `${testActions.INSTANCE_EATD_APPLY_CHANGES_PERSON}_FAILURE`:
      return handlePersonFailure(state, action, 'isApplying');

      // Preview Changes for Person
    case `${testActions.INSTANCE_EATD_PREVIEW_CHANGES_PERSON}_REQUEST`:
      return handlePersonRequest(state, action, 'isPreviewing');

    case `${testActions.INSTANCE_EATD_PREVIEW_CHANGES_PERSON}_SUCCESS`: {
      const previewPersonId = action.meta.personId;
      // Check if person exists before update (might have been removed)
      if (!state.testPersons?.[previewPersonId]) return state;
      return updatePersonState(state, previewPersonId, {
        isPreviewing: false,
        previewResult: action.payload,
        name: action.payload?.data?.originalData?.dynamicName || state.testPersons[previewPersonId].name,
        error: null,
      });
    }

    case `${testActions.INSTANCE_EATD_PREVIEW_CHANGES_PERSON}_FAILURE`:
      return handlePersonFailure(state, action, 'isPreviewing');

    case `${INSTANCE_EATD_COUNT_MATCHING_PERSONS}_REQUEST`:
      return {
        ...state,
        isCountingPersonMatches: true,
        personMatchCountError: null,
        error: null,
      };

    case `${INSTANCE_EATD_COUNT_MATCHING_PERSONS}_SUCCESS`:
      return {
        ...state,
        isCountingPersonMatches: false,
        personMatchCountError: null,
        personMatchCount: action.payload?.count ?? 0,
        error: null,
      };

    case `${INSTANCE_EATD_COUNT_MATCHING_PERSONS}_FAILURE`:
      return {
        ...state,
        isCountingPersonMatches: false,
        personMatchCountError: action.payload,
        error: action.payload,
      };

    case `${INSTANCE_EATD_TEST_CHANGE_ADD}_REQUEST`: {
      return {
        ...state,
        isAddingChange: true,
        addChangeError: null,
        error: null,
      };
    }

    case `${INSTANCE_EATD_TEST_CHANGE_ADD}_SUCCESS`: {
      const { changeId } = action.meta;
      const changeData = action.payload?.topdeskChange || {};
      if (!changeId) {
        return {
          ...state,
          isAddingChange: false,
        };
      }
      return {
        ...state,
        isAddingChange: false,
        addChangeError: null,
        error: null,
        testChanges: {
          ...state.testChanges,
          [changeId]: {
            ...(state.testChanges[changeId] || {}),
            ...buildChangeTestItem(changeId, changeData),
          },
        },
      };
    }

    case `${INSTANCE_EATD_TEST_CHANGE_ADD}_FAILURE`:
      return {
        ...state,
        isAddingChange: false,
        addChangeError: action.payload,
        error: action.payload,
      };

    case `${INSTANCE_EATD_PREVIEW_CHANGES_CHANGE}_REQUEST`: {
      const { changeId } = action.meta;
      return updateChangeState(state, changeId, {
        isPreviewing: true,
        isLoading: true,
        error: null,
      });
    }

    case `${INSTANCE_EATD_PREVIEW_CHANGES_CHANGE}_SUCCESS`: {
      const { changeId } = action.meta;
      return updateChangeState(state, changeId, {
        isPreviewing: false,
        isLoading: false,
        error: null,
        previewResult: action.payload,
        number: action.payload?.data?.originalData?.number || state.testChanges?.[changeId]?.number,
        name: action.payload?.data?.originalData?.number || changeId,
      });
    }

    case `${INSTANCE_EATD_PREVIEW_CHANGES_CHANGE}_FAILURE`: {
      const { changeId } = action.meta;
      return updateChangeState(state, changeId, {
        isPreviewing: false,
        isLoading: false,
        error: action.payload,
      });
    }

    case `${INSTANCE_EATD_APPLY_CHANGES_CHANGE}_REQUEST`: {
      const { changeId } = action.meta;
      return updateChangeState(state, changeId, {
        isApplying: true,
        error: null,
        applyResult: null,
      });
    }

    case `${INSTANCE_EATD_APPLY_CHANGES_CHANGE}_SUCCESS`: {
      const { changeId } = action.meta;
      return updateChangeState(state, changeId, {
        isApplying: false,
        error: null,
        applyResult: action.payload,
      });
    }

    case `${INSTANCE_EATD_APPLY_CHANGES_CHANGE}_FAILURE`: {
      const { changeId } = action.meta;
      return updateChangeState(state, changeId, {
        isApplying: false,
        error: action.payload,
        applyResult: null,
      });
    }

    case `${INSTANCE_EATD_COUNT_MATCHING_CHANGES}_REQUEST`:
      return {
        ...state,
        isCountingChangeMatches: true,
        changeMatchCountError: null,
        error: null,
      };

    case `${INSTANCE_EATD_COUNT_MATCHING_CHANGES}_SUCCESS`:
      return {
        ...state,
        isCountingChangeMatches: false,
        changeMatchCountError: null,
        changeMatchCount: action.payload?.count ?? 0,
        error: null,
      };

    case `${INSTANCE_EATD_COUNT_MATCHING_CHANGES}_FAILURE`:
      return {
        ...state,
        isCountingChangeMatches: false,
        changeMatchCountError: action.payload,
        error: action.payload,
      };

    case testActions.INSTANCE_EATD_TEST_CHANGE_REMOVE: {
      const removeChangeId = action.payload.changeId;
      return {
        ...state,
        testChanges: _.omit(state.testChanges, removeChangeId),
        error: null,
      };
    }

    // --- END: Added Person Test Cases ---

    default:
      return state;
  }
}
